import { memo, PropsWithChildren } from 'react';
import { Stack, SxProps, Typography } from '@mui/material';
import { COLOR_ACCENT, COLOR_BG_SECONDARY, COLOR_PRIMARY, TokenFormat } from 'shared';
import CopyField from 'shared/ui/copy-field';

import { WarningBox } from './warning-box';

export const DebitWarning = memo(({ children }: PropsWithChildren) => {
  return (
    <WarningBox>
      <Typography variant="body2" className="secondary">
        <Typography component="span" variant="body2" className="primary">
          Warning:{' '}
        </Typography>
        <Typography component="span" variant="body2" className="accent">
          {children}
        </Typography>
        <br />
        The order and price is only valid for 4 hours, if no payment is received during the specified time, your order
        will be canceled and you will need to start again. Transfer crypto under your own responsibility, overpaid coins
        won`t be refunded.
      </Typography>
    </WarningBox>
  );
});

interface DebitHintBaseProps {
  packageName: string;
  tokenDecimals?: number;
  tokenAmount: string;
  tokenSymbol: string;
  tokenNetworkShortName: string;
  sx?: SxProps;
}

interface DebitHintProps extends DebitHintBaseProps {
  paymentAddress?: string;
  showHintText?: boolean;
}

export const DebitHint = ({
  packageName,
  paymentAddress,
  tokenAmount,
  tokenDecimals,
  showHintText = true,
  tokenNetworkShortName,
  tokenSymbol,
  sx,
}: DebitHintProps) => {
  return (
    <Stack width="100%" bgcolor={COLOR_BG_SECONDARY} alignItems="flex-start" borderRadius={3} p={1.5} mb={2} sx={sx}>
      {showHintText && (
        <Typography variant="body2" className="secondary" mb={1.5}>
          To purchase{' '}
          <Typography component="span" variant="body2" className="accent">
            {packageName} package
          </Typography>{' '}
          buy{' '}
          <TokenFormat
            decimals={tokenDecimals}
            amount={tokenAmount}
            symbol={tokenSymbol}
            maxDecimalDigits={8}
            component="span"
            variant="body2"
            className="primary"
          />{' '}
          <Typography component="span" variant="body2" className="primary">
            ({tokenNetworkShortName})
          </Typography>{' '}
          in the Widget above. Make sure that receiving amount after fees deduction isn’t less than required amount.
          Otherwise, the purchase won’t be completed. <br /> As crypto recipient you must input the following address in
          the Onramper:
        </Typography>
      )}

      {paymentAddress && (
        <CopyField
          displayValue={paymentAddress}
          copyValue={paymentAddress}
          successCopyMessage="Address copied"
          btnSx={{
            color: COLOR_ACCENT,
            '&:hover': { color: COLOR_PRIMARY },
          }}
        />
      )}
    </Stack>
  );
};

interface DebitHintNativeTokenProps extends DebitHintBaseProps {
  paymentAddress: string;
}

export const DebitHintNativeToken = ({
  paymentAddress,
  tokenAmount,
  tokenDecimals,
  tokenSymbol,
  packageName,
  tokenNetworkShortName,
  sx,
}: DebitHintNativeTokenProps) => {
  return (
    <Stack
      width="100%"
      bgcolor={COLOR_BG_SECONDARY}
      alignItems="flex-start"
      borderRadius={3}
      p={1.5}
      mb={2}
      spacing={1}
      sx={sx}
    >
      <Typography variant="body2" className="secondary" mb={1.5}>
        <Typography component="span" variant="body2" className="accent">
          Step 1:{' '}
        </Typography>
        Buy at least{' '}
        <TokenFormat
          decimals={tokenDecimals}
          amount={tokenAmount}
          symbol={tokenSymbol}
          maxDecimalDigits={8}
          component="span"
          variant="body2"
          className="primary"
        />{' '}
        <Typography component="span" variant="body2" className="primary">
          ({tokenNetworkShortName})
        </Typography>{' '}
        in the Widget above. Input{' '}
        <Typography component="span" variant="body2" className="primary">
          your own wallet
        </Typography>{' '}
        as crypto recipient.
      </Typography>

      <WarningBox p={0} gap={0.75}>
        <Typography variant="body2" className="secondary">
          You will lose your funds if you input any other wallet.
        </Typography>
      </WarningBox>

      <Typography variant="body2" className="secondary" mb={1.5}>
        <Typography component="span" variant="body2" className="accent">
          Step 2:{' '}
        </Typography>
        Transfer{' '}
        <TokenFormat
          decimals={tokenDecimals}
          amount={tokenAmount}
          symbol={tokenSymbol}
          maxDecimalDigits={8}
          component="span"
          variant="body2"
          className="primary"
        />{' '}
        <Typography component="span" variant="body2" className="primary">
          ({tokenNetworkShortName})
        </Typography>{' '}
        from{' '}
        <Typography component="span" variant="body2" className="primary">
          your own wallet
        </Typography>{' '}
        to purchase{' '}
        <Typography component="span" variant="body2" className="accent">
          {packageName} package
        </Typography>{' '}
        . Make sure that receiving amount after fees deduction isn’t less than required amount. Otherwise, the purchase
        won’t be completed. <br /> Transfer the amount above to the{' '}
        <Typography component="span" variant="body2" className="primary">
          following address:
        </Typography>
      </Typography>

      <CopyField
        displayValue={paymentAddress}
        copyValue={paymentAddress}
        successCopyMessage="Address copied"
        btnSx={{
          color: COLOR_ACCENT,
          '&:hover': { color: COLOR_PRIMARY },
        }}
      />
    </Stack>
  );
};
